import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import { cn, Gallery, NavDots, Scroller, YandexShare } from '@divlab/divanui';
import { IconShare } from '@divlab/divanui/icons';

import * as PageProductStore from '@Stores/PageProduct';
import * as ModalWindows from '@Stores/ModalWindows';
import { useDeps } from '@Contexts/DI';
import ApiProduct from '@Api/Product';
import useMedias from '@Hooks/useMedias';
import Img from '@UI/Img';
import ProductLike from '@Components/ProductLike';
import ProductImage from '@Pages/PageProduct/elements/ProductImage';
import ModelViewerAr from '../ModelViewerAr';
import ButtonAr from '../ButtonAr';
import Buttons from '../Buttons';
import LazyProductModel from '../ProductModel/LazyProductModel';
import Back from '../Back';
import VideoPreview from './elements/VideoPreview';
import styles from './PhotoGallery.module.css';

import type { FC, HTMLAttributes, PropsWithChildren } from 'react';
import type { AR } from '@Types/AR';
import type { IGalleryRef, SwiperClass } from '@divlab/divanui';
import type { ProductData } from '@Types/Product';
import type { ConfigurationData, MediaGallery } from '@Pages/PageProduct/typings';

export interface PhotoGalleryProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  images: MediaGallery;
  ar?: AR;
  product: ProductData;
  configuration: ConfigurationData;
  productId: number;
}

export interface ItemsProps {
  className?: string;
  slideIndex: number;
  onChangeCurrent?(index: number): void;
}

const Items: FC<PropsWithChildren<ItemsProps>> = (props) => {
  const { children, onChangeCurrent, ...restProps } = props;
  const { isDesktop } = useMedias();

  const handleSlideChange = useCallback(
    (swiper: SwiperClass) => {
      onChangeCurrent?.(swiper.realIndex);
    },
    [onChangeCurrent],
  );

  return isDesktop ? (
    <Gallery {...restProps} onSlideChange={handleSlideChange}>
      {children}
    </Gallery>
  ) : (
    <Scroller {...restProps} invisible>
      {children}
    </Scroller>
  );
};

const PhotoGallery: FC<PhotoGalleryProps> = (props) => {
  const { ar, images, product, configuration, productId, ...restProps } = props;
  const { logger } = useDeps();
  const { isMobileM, isMobile } = useMedias();
  const [slideIndex, setSlideIndex] = useState(0);
  const [productsOnPins, setProductsOnPins] = useState<ProductData[]>(null);
  const idViewerModel = 'pageProduct';
  const viewerModel = PageProductStore.useViewerModel(idViewerModel);
  const { image, cylindo, constructorHash, id, parameterValues } = configuration;
  const hasCylindo = !!cylindo;
  const mainGalleryRef = useRef<IGalleryRef>(null);

  const isOpen360 = useMemo(() => {
    return isMobileM ? viewerModel?.isOpen360 : hasCylindo && slideIndex === 0;
  }, [hasCylindo, isMobileM, slideIndex, viewerModel?.isOpen360]);

  const allPhotos = useMemo(() => {
    const firstMedias = images.grid.flat();
    if (!images.other?.length) return firstMedias;

    let photos = firstMedias.concat(images.other);

    if (hasCylindo && !isMobileM) {
      const [firstPhoto] = photos;
      photos = [{ ...firstPhoto, isCylindo: true }, ...photos];
    }

    return photos;
  }, [hasCylindo, images, isMobileM]);

  const allPinProductsIds = useMemo(() => {
    const productIds = [];

    allPhotos.forEach((photo) => {
      photo.products.forEach((pr) => productIds.push(pr.id));
    });

    const uniqProductsIds = Array.from(new Set(productIds));

    return uniqProductsIds;
  }, [allPhotos]);

  const load = useCallback(async () => {
    try {
      if (allPinProductsIds.length === 0) return;

      const res = await ApiProduct.getProducts(allPinProductsIds);

      if (res.length === 0) {
        throw Error("Product wasn't loaded");
      }
      setProductsOnPins(res);
    } catch (err) {
      logger.log(err);
    }
  }, [allPinProductsIds, logger]);

  const handleClickImage = useCallback(
    (startSlideIndex: number) => {
      if (window.cancelClick) return;

      ModalWindows.open('ProductPhotos', {
        images: hasCylindo ? [...allPhotos].splice(1) : allPhotos,
        startSlideIndex,
      });
    },
    [allPhotos, hasCylindo],
  );

  const handleClickVideo = useCallback(() => {
    if (window.cancelClick) return;

    ModalWindows.open('Video', {
      videoId: allPhotos[slideIndex].video,
    });
  }, [allPhotos, slideIndex]);

  const handleSlideChange = useCallback((swiper: SwiperClass) => {
    setSlideIndex(swiper.realIndex);
  }, []);

  const handleChangeCurrent = useCallback((index: number) => {
    if (!mainGalleryRef.current) return;

    setSlideIndex(index);
    mainGalleryRef.current.slideTo(index);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div {...restProps} className={cn(styles.photogallery)}>
      <div className={styles.topPanel}>
        <div className={styles.back}>
          <Back />
        </div>
        <div className={styles.right}>
          {isMobileM && (
            <div className={styles.wrapperIcon}>
              <YandexShare
                services={['telegram', 'whatsapp', 'vkontakte']}
                slotTrigger={<IconShare />}
              />
            </div>
          )}

          <div className={styles.wrapperIcon}>
            <ProductLike
              product={{
                ...product,
                id,
                constructorParameterValues: parameterValues as any,
              }}
              hash={constructorHash}
            />
          </div>
        </div>
      </div>
      <div className={styles.containerImages}>
        {!isMobileM && ar && <ButtonAr className={styles.button} idViewerModel={idViewerModel} />}
        <div className={styles.galleryContainer}>
          {!isMobileM && ar && (
            <CSSTransition
              timeout={200}
              classNames={{
                enter: styles.enter,
                enterActive: styles.enterActive,
                exit: styles.exit,
                exitActive: styles.exitActive,
              }}
              in={viewerModel?.isOpenAr}
              unmountOnExit
            >
              <div className={styles.modelViewerArWrapper}>
                <ModelViewerAr
                  className={styles.modelViewerAr}
                  ar={ar}
                  view='gallery'
                  idViewerModel={idViewerModel}
                />
              </div>
            </CSSTransition>
          )}
          {hasCylindo && (
            <LazyProductModel
              className={cn(styles.cylindo, { [styles.isActive]: isOpen360 })}
              medias={cylindo}
            />
          )}
          <Gallery ref={mainGalleryRef} slidesPerView={1} onSlideChange={handleSlideChange}>
            {allPhotos.map((item, index) => (
              <div key={item.image} className={styles.slide}>
                {item.video ? (
                  <VideoPreview
                    src={item.image}
                    cnPreviewHeight={styles.videoHeight}
                    onClick={handleClickVideo}
                  />
                ) : (
                  <>
                    {item.isCylindo ? (
                      <div />
                    ) : (
                      <ProductImage
                        img={item}
                        size='m'
                        products={productsOnPins}
                        onClickImage={() => handleClickImage(hasCylindo ? index - 1 : index)}
                        loading={index !== 0 ? 'lazy' : 'eager'}
                        productId={productId}
                      />
                    )}
                  </>
                )}
              </div>
            ))}
          </Gallery>
        </div>

        {allPhotos.length > 1 && isMobileM && (
          <div className={styles.wrapperDots}>
            <NavDots count={allPhotos.length} slideIndex={slideIndex} view='filled' />
          </div>
        )}
        <Items className={styles.items} slideIndex={slideIndex}>
          {allPhotos.map((item, index) => (
            <div
              className={cn(styles.containerImage, {
                [styles.firstImage]: index === 0,
                [styles.active]: slideIndex === index,
              })}
              key={index}
              onClick={() => handleChangeCurrent(index)}
            >
              {item.video ? (
                <VideoPreview src={item.image} cnPreviewHeight={styles.videoHeight} />
              ) : (
                <Img
                  itemProp='image'
                  alt=''
                  className={cn(styles.imageWrapper, {
                    [styles.cylindoThumb]: item.isCylindo,
                  })}
                  cnImage={styles.image}
                  src={item.isCylindo ? image : item.image}
                  loading={index !== 0 ? 'lazy' : 'eager'}
                />
              )}
            </div>
          ))}
        </Items>
      </div>

      {isMobileM && (
        <Buttons
          ar={ar}
          className={styles.buttons}
          scrollable={isMobile}
          size='s'
          idViewerModel={idViewerModel}
          hasCylindo={hasCylindo}
        />
      )}
    </div>
  );
};

export default memo(PhotoGallery);
